/**
 * tenant-vocab — served tenant vocabulary + navigation composition.
 *
 * Docket 2026-07-26 §1.2 (the "resolved lookup taxonomy" class): tenant
 * vocabulary — lines of business, customer tiers/labels, storeops catalogs,
 * requisition roles, per-persona rail composition — must NOT live in app
 * source as id-keyed constants. It arrives through the typed
 * @v0y4ge/api-client (ops declared here, REQ-091), backed by @v0y4ge/api-mocks
 * in the wiring kit; in the design preview the root vocab-preview-seeds.js
 * serves the same shapes `__source: "cd-preview-seed"`-marked. Screens render
 * empty / the raw id / a neutral value until the catalog resolves.
 *
 * Anonymous surfaces NEVER trigger these calls (both ops are /v1/admin/* —
 * same acceptance as the §2.5 public/admin split): ensure() no-ops until a
 * session exists, so public journey/hub/event pages just render raw-id
 * fallbacks.
 */
import { useEffect, useState } from "react";
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../shell/opSource";
import { getActiveTenantSlug, isSignedIn } from "../shell/access";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

/* ── Shapes (REQ-091 asks for exactly these, served per tenant) ────────── */
export interface LobDef { id: string; name: string; color: string }
export interface TierDef { id: string; label: string; bg: string; fg: string }
/** Declarative money display — functions can't be served; the client formats. */
export interface MoneySpec { kind: "currency" | "credits"; symbol?: string }
export interface CustomersVocabData {
  accent: string; accentSoft: string; ink: string;
  entity: string; entityPlural: string;
  money: MoneySpec; cardMoney: MoneySpec;
  tiers: TierDef[];                         // ordered — order IS tierOrder
  channelLabels: Record<string, string>;    // channel id -> label (icons/tints are semantic UI, client-side)
  queueServedLabel: string;                 // "Served" / "Seen"
  columns: { store: string; ltv: string };
  kpis: { customers: string; appointments: string; appointmentsSub: string; events: string; eventsSub: string; queue: string; queueSub: string };
  prefsLabels: { store: string; day: string; time: string; channel: string; category: string; ytd: string };
  otb: { hasCardTitle: string; metricSub: string; balanceWord: string; limitWord: string; noCardTitle: string; noCardSub: string; inviteBtn: string; inviteToastSub: string };
  searchPlaceholder: string;
  exportTitle: string;
  emailDomain: string;
  phonePrefix: string;                      // display prefix for the derived contact line (REQ-091 notes the record is the canonical home)
}
/** Store-ops ZONE tints only. The service-category / client-tier unions and
 * SERVICE_CATEGORY_CODES are CONTRACT-owned (docket §1.2.10: their closed Zod
 * enums in api-contracts/storeops.ts get revised on Claude Code's side first)
 * — deliberately NOT served or reshaped here. */
export interface StoreOpsVocabData {
  zones: { id: string; tint: "blue" | "orange" | "gold" | "purple" | "turquoise" | "neutral" }[];
}
export interface TenantVocabulary {
  linesOfBusiness: LobDef[];
  customers: CustomersVocabData | null;
  storeops: StoreOpsVocabData | null;
  requisitionRoles: string[];
  __source?: string;
}
export interface NavCompositionItem { id: string; label: string; labelKey: string; icon: string; needs?: string | string[]; bottom?: boolean; badge?: number }
export interface NavCompositionProfile { process: "workforce" | "storeops" | "events"; role: string; landing: string; items: NavCompositionItem[]; /** Served seed for the Pinned+All rail trial (2026-08-06) — highest-value surfaces per persona. */ defaultPins?: string[] }
export interface NavComposition {
  /** Per-persona rail profiles for THIS tenant (server-scoped — no slug lists client-side). */
  profiles: NavCompositionProfile[];
  /** Server-enabled ids for rail items marked `gated` (process surfaces this tenant runs). */
  railItemIds: string[];
  __source?: string;
}

/* ── Declared ops (REQ-091) ────────────────────────────────────────────── */
const q = (slug: string | null) => (slug ? `?tenant=${encodeURIComponent(slug)}` : "");
export const getTenantVocabulary = (slug: string | null) =>
  tag("getTenantVocabulary", apiRaw<TenantVocabulary>(`/v1/admin/tenant/vocabulary${q(slug)}`, undefined, 'getTenantVocabulary'));
export const getNavComposition = (slug: string | null) =>
  tag("getNavComposition", apiRaw<NavComposition>(`/v1/admin/tenant/navigation${q(slug)}`, undefined, 'getNavComposition'));

/* ── Store — one cache per active tenant, honest null until resolved ───── */
type CacheState = "idle" | "loading" | "ready" | "error";
let C: { slug: string | null; vocab: TenantVocabulary | null; nav: NavComposition | null; state: CacheState } =
  { slug: null, vocab: null, nav: null, state: "idle" };
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => { try { f(); } catch { /* unmounted */ } });

/* The in-flight composition fetch, exposed so sign-in can START it and then
 * AWAIT it (docket 2026-09-16, Ask 3): the served landing was resolved by a
 * 1500 ms poll whose first tick also kicked off this fetch, and on a real
 * tenant the answer arrived ~5 s later — so every served landing lost the race
 * and the persona landed on the role fallback. Resolves as soon as the pair
 * settles; null when there is nothing to fetch (signed out). */
let inflight: Promise<void> | null = null;

function ensure(): void {
  let slug: string | null = null, signed = false;
  try { slug = getActiveTenantSlug(); signed = isSignedIn(); } catch { /* boot order */ }
  if (!signed) return; // public pages: zero /v1/admin/* calls
  if (C.state !== "idle" && C.slug === slug) return;
  const mySlug = slug;
  C = { slug: mySlug, vocab: null, nav: null, state: "loading" };
  inflight = Promise.allSettled([getTenantVocabulary(mySlug), getNavComposition(mySlug)]).then(([v, n]) => {
    if (C.slug !== mySlug) return; // tenant switched mid-flight
    C = {
      slug: mySlug,
      vocab: v.status === "fulfilled" ? v.value : null,
      nav: n.status === "fulfilled" ? n.value : null,
      state: v.status === "fulfilled" || n.status === "fulfilled" ? "ready" : "error",
    };
    notify();
  });
}

/** Start the tenant vocabulary + nav-composition fetch (if not already running)
 *  and resolve when it has settled. Call it right after `signIn()` so the fetch
 *  overlaps the rest of sign-in, then await it where the served landing is
 *  needed. Resolves immediately once the context is ready. */
export function ensureTenantContext(): Promise<void> {
  ensure();
  if (C.state === "ready" || C.state === "error") return Promise.resolve();
  return inflight ?? Promise.resolve();
}

/** Where the composition fetch stands — sign-in reads this AFTER awaiting
 *  `ensureTenantContext()` to tell "the tenant served nothing for this role"
 *  (fall back to the assignment landing) from "the fetch failed" (offer a
 *  retry; never silently land somewhere else). 2026-09-17 sign-in sequencing. */
export function getTenantContextState(): CacheState { return C.state; }
/** Drop the cached composition so the next `ensureTenantContext()` refetches —
 *  the sign-in overlay's Retry after a failed fetch. */
export function resetTenantContext(): void { C = { slug: null, vocab: null, nav: null, state: "idle" }; inflight = null; }

/** Sync read (starts the fetch lazily). Null until served — callers fall back to raw ids / neutral. */
export function getTenantVocab(): TenantVocabulary | null { ensure(); return C.vocab; }
export function getNavComp(): NavComposition | null { ensure(); return C.nav; }
export function useTenantVocab(): TenantVocabulary | null {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((x) => x + 1); subs.add(f); ensure(); return () => { subs.delete(f); }; }, []);
  return getTenantVocab();
}
export function useNavComposition(): NavComposition | null {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((x) => x + 1); subs.add(f); ensure(); return () => { subs.delete(f); }; }, []);
  return getNavComp();
}

/* ── Display helpers ───────────────────────────────────────────────────── */
export function formatVocabMoney(spec: MoneySpec | undefined, n: number): string {
  if (!spec) return String(n);
  if (spec.kind === "credits") return n ? `${n} cr` : "—";
  const s = spec.symbol || "";
  return n >= 1000 ? s + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k" : s + n;
}
